import { Alert, Autocomplete, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, FormGroup, Grid, ListItemText, TextField, Typography, useTheme } from '@mui/material';
import { FormEvent, SyntheticEvent, useContext, useEffect, useState } from 'react';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { ItemActivityReportRequest, PrimaryCategory2, Store } from '../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { formatDateForReport, formatDateTimeForDisplay, filterStoreOptions } from '../../../utils';
import { UserContext } from '../../../components/shared/useUser';
import { StoreScope } from '../components/StoreScopePicker';
import QuickDateFilters, { DateRange } from '../components/QuickDateFilters';
import AdvancedFilters from '../components/AdvancedFilters';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import dayjs from 'dayjs';
import useShiftConfiguration from '../../../hooks/configurations/useShiftConfiguration';

type IProps = {
  /** Fired after a report is successfully queued so the page can refresh status panels. */
  onGenerated?: () => void;
  onGenerationFailed?: (message?: string) => void;
  storeScope?: StoreScope;
  /** Full scope pickers rendered inside the merged Advanced filters collapse. */
  advancedScopeContent?: React.ReactNode;
}

const ALL_CATEGORIES_ID = -1;

const ItemActivityReportForm = (props: IProps) => {
  const { selectedStore, user } = useContext(UserContext);
  const [allCategories, setAllCategories] = useState<PrimaryCategory2[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<PrimaryCategory2[]>([]);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [manualDateEditCount, setManualDateEditCount] = useState(0);
  const [toDate, setToDate] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  // Flipped on failed submit validation so required fields render their error state.
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState<boolean>(false);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);
  const { getPrimaryCategoriesForStore, runItemActivityReport } = useNswagClient();
  const { t } = useTranslation('common');
  const theme = useTheme();

  const { calculateOperationalReportsRange } = useShiftConfiguration();
  const [operationalStart, setOperationalStart] = useState<Date | null>(null);
  const [operationalEnd, setOperationalEnd] = useState<Date | null>(null);
  const [isShiftEnabled, setIsShiftEnabled] = useState(true);

  // Categories load from a store in the active scope: the first explicitly scoped store, else any
  // store of the scoped franchise, else the globally selected store (legacy/unscoped path).
  const categoryStoreNumber =
    props.storeScope?.storeNumbers[0]
    ?? user?.stores?.find((s) => s.franchiseId === props.storeScope?.franchiseId)?.storeNumber
    ?? selectedStore?.storeNumber;

  useEffect(() => {
    if (categoryStoreNumber) {
      setSelectedCategories([]);
      loadCategories(categoryStoreNumber);
    }
  }, [categoryStoreNumber]);

  useEffect(() => {
    if (!fromDate || !toDate) return;

    const selectedAllStores =
      checkedAllStores || (selectedStores.length > 0 && selectedStores.length === user?.stores?.length);

    const store = selectedAllStores
      ? user?.stores?.[0]
      : selectedStores?.[0];

    if (!store?.storeNumber) return;

    const systemFranchises = user?.franchises?.filter(f =>
      user?.stores?.some(s => s.franchiseId === f.franchiseId),
    ) || [];

    const matchingFranchise = systemFranchises.find(f =>
      f.franchiseId === store?.franchiseId);

    calculateOperationalReportsRange(
      new Date(fromDate),
      new Date(toDate),
      store.storeNumber,
      matchingFranchise?.franchiseName ?? '',
    ).then(({ start, end, isShiftEnabled }) => {
      setIsShiftEnabled(isShiftEnabled);
      setOperationalStart(start);
      setOperationalEnd(end);
    });
  }, [fromDate, toDate, selectedStores, checkedAllStores, user?.stores]);

  const loadCategories = (storeNumber: string) => {
    getPrimaryCategoriesForStore(storeNumber).then((res) => {
      if (res.data) {
        setAllCategories(res.data);
        // No pre-selection: empty selection means "All Categories" (shown as a chip).
      }
    }).catch((error) => {
      console.log(error);
    });
  };

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setSelectedStores([]);

    setCheckedAllStores(checked);
  };

  const getMaxDate = () => {
    const today = dayjs().format('YYYY-MM-DD');
    return today;
  };

  const getMinDate = () => {
    const minDate = new Date('1900-01-01').toISOString().split('T')[0];
    return minDate;
  };

  const handleFromDateChange = (value: string | null) => {
    setFromDate(value ?? '');
    setManualDateEditCount((c) => c + 1);
  };

  const handleToDateChange = (value: string | null) => {
    setToDate(value ?? '');
    setManualDateEditCount((c) => c + 1);
  };

  const handleQuickDateRange = (range: DateRange) => {
    setFromDate(range.from);
    setToDate(range.to);
  };

  const handleCategorySelectChange = (event: SyntheticEvent, value: PrimaryCategory2[]) => {
    // The synthetic "All Categories" option resets back to the implicit all-categories state.
    setSelectedCategories(value.some((c) => c.id === ALL_CATEGORIES_ID) ? [] : value);
  };

  const handleSelectChange = (event: SyntheticEvent, value: Store[]) => {
    setSelectedStores(value);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    // Both range dates are required — submitting without them sends a bad request.
    if (!fromDate || !toDate || !dayjs(fromDate).isValid() || !dayjs(toDate).isValid()) {
      setShowErrors(true);
      setErrorMessage(t('reportingV2.date-range-required'));
      return;
    }
    setIsGenerating(true);
    setErrorMessage('');

    const from = dayjs(fromDate).startOf('day').toDate();
    const to = dayjs(toDate).endOf('day').toDate();
    const selectedAllStores = checkedAllStores || (selectedStores.length > 0 && selectedStores.length == user?.stores?.length);
    const scopedByPicker = !!props.storeScope;

    const request: ItemActivityReportRequest = {
      reportOnAllAvailableStores: scopedByPicker ? props.storeScope!.storeNumbers.length === 0 : selectedAllStores,
      primaryCategoryIds: selectedCategories.length === 0 ? [] : selectedCategories.map(x => x.id as number),
      stores: scopedByPicker
        ? (props.storeScope!.storeNumbers.length > 0 ? props.storeScope!.storeNumbers : undefined)
        : (selectedAllStores ? undefined : selectedStores.map(x => x.storeNumber as string)),
      from: formatDateForReport(from),
      to: formatDateForReport(to),
      tenantId: props.storeScope?.tenantId,
      franchiseId: props.storeScope?.franchiseId,
    };

    runItemActivityReport(request).then(() => {
      setIsRequestSubmitted(true);
      props.onGenerated?.();
    }).catch((error) => {
      props.onGenerationFailed?.(typeof (error) === 'string' ? error : undefined);
      console.log(error);
    }).finally(() => {
      setIsGenerating(false);
      setIsRequestSubmitted(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}
      noValidate>
      {fromDate && toDate && operationalStart && operationalEnd && isShiftEnabled && (
        <Box
          sx={{
            mt: 2,
            display: 'inline-block',
            px: 2,
            py: 2,
            mb: 5,
            border: `1px solid ${theme.palette.secondary[300]}`,
            borderRadius: '8px',
          }}
        >
          <Typography
          >
            {t('shiftingConfiguration.filterPeriodApplied', {
              operationalStart: formatDateTimeForDisplay(operationalStart),
              operationalEnd: formatDateTimeForDisplay(operationalEnd),
            })}
          </Typography>
        </Box>
      )}
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        {!props.storeScope && (
          <>
            <Grid item
              sm={6}
              xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  multiple
                  limitTags={2}
                  options={user?.stores ?? []}
                  value={selectedStores}
                  onChange={handleSelectChange}
                  disabled={checkedAllStores}
                  getOptionKey={(option) => option.storeNumber ?? ''}
                  getOptionLabel={(option) => option.storeName ?? ''}
                  filterOptions={filterStoreOptions}
                  renderInput={(params) => (
                    <TextField {...params}
                      label={t('reporting.select-stores')}
                      placeholder={t('reporting.select-stores')} />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item
              sm={6}
              xs={12}>
              <FormGroup style={{ textAlign: 'right' }}>
                <FormControlLabel
                  control={<Checkbox
                    checked={checkedAllStores}
                    onChange={handleSelectAllChange} />}
                  label={t('reporting.all-stores')}
                />
              </FormGroup>
            </Grid>
          </>
        )}
        {/* Quick presets stay visible: date chips + an "All categories" chip. The full date pickers
            and category dropdown live under Advanced filters. */}
        <Grid item
          xs={12}>
          <QuickDateFilters onChange={handleQuickDateRange}
            manualEditSignal={manualDateEditCount} />
        </Grid>
        <Grid item
          xs={12}
          sx={{ '&&': { paddingTop: '30px' } }}>
          <Typography variant="textMD"
            sx={{ fontWeight: 600, mb: '6px', display: 'block' }}>
            {t('reportingV2.categories-label')}
          </Typography>
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Chip
              label={t('reporting.all-categories')}
              clickable
              onClick={() => setSelectedCategories([])}
              variant={selectedCategories.length === 0 ? 'filled' : 'outlined'}
              aria-pressed={selectedCategories.length === 0}
              sx={{
                fontWeight: selectedCategories.length === 0 ? 600 : 400,
                bgcolor: selectedCategories.length === 0 ? theme.palette.primary.main : 'transparent',
                color: selectedCategories.length === 0 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                borderColor: selectedCategories.length === 0 ? theme.palette.primary.main : theme.palette.custom.gray[300],
                '&:hover': { bgcolor: selectedCategories.length === 0 ? theme.palette.primary.dark : theme.palette.custom.gray[100] },
              }}
            />
          </Box>
        </Grid>
        <Grid item
          xs={12}>
          <AdvancedFilters>
            <Grid container
              spacing={10}>
              {props.advancedScopeContent && (
                <Grid item
                  xs={12}>
                  {props.advancedScopeContent}
                </Grid>
              )}
              <Grid item
                sm={6}
                xs={12}>
                <DatePickerLocalized
                  label={t('reporting.start-date')}
                  value={fromDate || null}
                  useCalendar={false}
                  error={showErrors && (!fromDate || !dayjs(fromDate).isValid())}
                  onChange={handleFromDateChange}
                  shouldDisableDate={(date) => date < getMinDate() || date > (toDate !== '' ? toDate : getMaxDate())}
                  translations={{
                    clearButtonLabel: t('calendar.clearButtonLabel'),
                    todayButtonLabel: t('calendar.todayButtonLabel'),
                    cancelButtonLabel: t('calendar.cancelButtonLabel'),
                    okButtonLabel: t('calendar.okButtonLabel'),
                    datePickerToolbarTitle: t('reporting.start-date'),
                  }}
                />
              </Grid>
              <Grid item
                sm={6}
                xs={12}>
                <DatePickerLocalized
                  label={t('reporting.end-date')}
                  value={toDate || null}
                  useCalendar={false}
                  error={showErrors && (!toDate || !dayjs(toDate).isValid())}
                  onChange={handleToDateChange}
                  shouldDisableDate={(date) => date < (fromDate !== '' ? fromDate : getMinDate()) || date > getMaxDate()}
                  translations={{
                    clearButtonLabel: t('calendar.clearButtonLabel'),
                    todayButtonLabel: t('calendar.todayButtonLabel'),
                    cancelButtonLabel: t('calendar.cancelButtonLabel'),
                    okButtonLabel: t('calendar.okButtonLabel'),
                    datePickerToolbarTitle: t('reporting.end-date'),
                  }}
                />
              </Grid>
              <Grid item
                xs={12}>
                <FormControl fullWidth>
                  <Autocomplete
                    fullWidth
                    multiple
                    disableCloseOnSelect
                    options={[{ id: ALL_CATEGORIES_ID, primaryCategoryName: t('reporting.all-categories') } as PrimaryCategory2, ...allCategories]}
                    value={selectedCategories}
                    onChange={handleCategorySelectChange}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.primaryCategoryName ?? ''}
                    renderOption={(props, option) => {
                      const isAll = option.id === ALL_CATEGORIES_ID;
                      const selected = isAll ? selectedCategories.length === 0 : selectedCategories.some((c) => c.id === option.id);
                      return (
                        <li {...props}
                          key={option.id}>
                          <Checkbox checked={selected}
                            sx={{ mr: 1 }} />
                          <ListItemText primary={option.primaryCategoryName} />
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((category, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={category.id}
                          label={category.primaryCategoryName}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params}
                        label={t('reporting.select-categories')}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {selectedCategories.length === 0 && (
                                <Chip
                                  label={t('reporting.all-categories')}
                                  size="small"
                                  sx={{ mr: '4px' }}
                                />
                              )}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }} />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </AdvancedFilters>
        </Grid>
        {
          errorMessage &&
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            disabled={isGenerating || isRequestSubmitted || (props.storeScope !== undefined && props.storeScope.franchiseId == null)}
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ItemActivityReportForm;